import * as React from 'react';
import groupBy from 'lodash-es/groupBy';
import { ApiDevices, Device } from '../misc/api/DeviceLinkApi/Devices';
import { DeviceCard } from './DeviceCard';
import { DeviceInteractionModal } from './DeviceInteractionModal';
import Alert, { AlertCategoryEnum } from '../misc/components/Alert';
import { PairDeviceLinkModal } from './PairDeviceLinkModal';
import { NoActiveDevicesPrompt } from '../device-return/components/no-active-devices-prompt';
import { CannotFindDeviceWizard } from '../cannot-find-device/cannot-find-device-wizard';
import { OmniCard } from './OmniCard';
import { OmniInteractionModal } from './OmniInteractionModal';
import { DeviceTypes } from '../misc/api/apiTypes';
import { DeviceEncryptionAlert } from '../misc/components/DeviceEncryptionAlert';
import DocumentTitle from '../misc/components/DocumentTitle';
import { CheckForUpdates } from '../misc/api/DeviceLinkApi/Updates';
import { initialDevicesState, devicesReducer, ReceiveDeviceInteractionsAction, LaunchInteractionModalAction, LaunchOmniInteractionModalAction, ReceiveDeviceNotificationsAction, RequestDevicesAction, ReceiveDevicesAction, UpdateStatus, SetDeviceAlertErrorAction, UploadStatus } from './DevicesNewReducer';
import { DeviceNotification } from '../misc/api/DeviceLinkApi/Models/DeviceNotification';
import * as VitaloJak1Api from '../misc/api/DeviceLinkApi/VitaloJak1';
import * as VitaloJak2Api from '../misc/api/DeviceLinkApi/VitaloJak2';
import { useSecurityContext } from '../Core/SecurityContext';
import { DevicesStateContextProvider } from './DevicesStateContext';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { useInterval } from '../misc/Hooks/UseInterval';
import { SessionTypes } from '../misc/api/ApiSessionTypes';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';
import { useIdleTimerContext } from '../Core/IdleTimerContext';
import { SendAlertToServer } from './AlertUtility';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

declare const window: Window & { V: any };

export const DevicesScreen = () => {
    const [pollingTimer, setPollingTimer] = React.useState<number | null>(15000);
    const [cannotFindDeviceWizardVisible, setCannotFindDeviceWizardVisible] = React.useState<boolean>(false);
    const [pairingModalVisible, setPairingModalVisible] = React.useState<boolean>(false);
    const [deviceLinkStatus, setDeviceLinkStatus] = React.useState(UpdateStatus.UpToDate);
    const [state, dispatch] = React.useReducer(devicesReducer, initialDevicesState);
    const { currentUser } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();
    const webPortalClient = useWebPortalApiClient();

    const { timer } = useIdleTimerContext();
    useInterval(() => fetchDevices(), pollingTimer);

    const pollingStop = () => {
        setPollingTimer(null);
    }

    const fetchDevices = async () => {
        try {
            const updated = await CheckForUpdates(
                () => {
                    pollingStop();
                    setDeviceLinkStatus(UpdateStatus.Updating);
                },
                () => {
                    setDeviceLinkStatus(UpdateStatus.UpToDate);
                },
                () => {
                    setDeviceLinkStatus(UpdateStatus.FailedToUpdate);
                });

            if (updated) {
                setPollingTimer(15000);
            }

            dispatch(RequestDevicesAction.create());
            const devices = await ApiDevices();

            dispatch(ReceiveDevicesAction.create(devices));
        }
        catch (err) {
            pollingStop();
            setPairingModalVisible(true);
        }
    }

    React.useEffect(() => {
        const init = async () => {
            timer.pause();
            await fetchDevices();
        }

        init();

        return () => { pollingStop(); timer.resume() }
    }, []);

    React.useEffect(() => {
        const pollingHandling = async () => {
            if (state.isInteractionModalVisible || state.isOmniInteractionModalVisible || cannotFindDeviceWizardVisible) {
                return pollingStop();
            }

            if (!pollingTimer) {
                await fetchDevices();
                setPollingTimer(15000);
            }
        }

        pollingHandling();
    }, [state.isInteractionModalVisible, state.isOmniInteractionModalVisible, cannotFindDeviceWizardVisible]);

    const GetDeviceInteractions = async (deviceType: DeviceTypes) => {
        const query = {
            Filters: [
                { Member: 'DeviceType.Id', Values: [deviceType] }
            ]
        }

        const interactionsResult = await legacyClient.deviceTypeInteractions(query);
        dispatch(ReceiveDeviceInteractionsAction.create(interactionsResult.Data));
    }



    const GetDeviceNotifications = async (selectedDevices: Array<Device>) => {
        let notifications: Array<DeviceNotification> = [];
        try {
            for (const device of selectedDevices) {
                switch (device.type) {
                    case DeviceTypes.VitaloJAK1: {
                        const receivedNotificationsVJ1 = await VitaloJak1Api.ApiGetDeviceNotifications(device.id);
                        notifications = notifications.concat(receivedNotificationsVJ1);
                        break;
                    }
                    case DeviceTypes.VitaloJAK2: {
                        const receivedNotificationsVJ2 = await VitaloJak2Api.ApiGetDeviceNotifications(device.id);
                        notifications = notifications.concat(receivedNotificationsVJ2);
                        break;
                    }
                }

                for (const notification of notifications) {
                    await SendAlertToServer(webPortalClient.client, notification);
                }
            }
        }
        catch (err) {
            dispatch(SetDeviceAlertErrorAction.create(err.message));
        }
        dispatch(ReceiveDeviceNotificationsAction.create(notifications));
    }

    const LaunchInteractionModal = async (selectedDevice: Device) => {
        await GetDeviceInteractions(selectedDevice.type);
        dispatch(LaunchInteractionModalAction.create(selectedDevice));
        await GetDeviceNotifications([selectedDevice]);
    }

    const LaunchOmniInteractionModal = async (selectedDeviceType: DeviceTypes) => {
        await GetDeviceInteractions(selectedDeviceType);
        dispatch(LaunchOmniInteractionModalAction.create(selectedDeviceType));

        await GetDeviceNotifications(state.devices.filter(x => x.type === selectedDeviceType));
    }

    const isVitalojakEnabled = () => {
        return study.sessionTypeIds && !!study.sessionTypeIds.find(st => st === SessionTypes.Cough);
    }

    const activeDeviceClick = async (device: Device) => {
        await LaunchInteractionModal(device);
    }

    const omniCardClick = async (deviceType: DeviceTypes) => {
        await LaunchOmniInteractionModal(deviceType);
    }

    const renderPairingModal = (): JSX.Element | JSX.Element[] => {
        return <PairDeviceLinkModal
            show={pairingModalVisible}
            updateStatus={deviceLinkStatus}
            squirrelUrl="https://devicelink.vitalograph-clinical.info/setup.exe"
            onClose={() => {
                fetchDevices();
                setPairingModalVisible(false);
                setPollingTimer(15000);
            }} />
    }

    const cannotFindDeviceClick = () => {
        setCannotFindDeviceWizardVisible(true);
    }

    const renderDevices = (): JSX.Element | JSX.Element[] => {
        if (state.isFetching) {
            return (
                <Alert category={AlertCategoryEnum.Info} show={true}>
                    <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" /> Searching for connected devices...
                </Alert>
            );
        } else if (deviceLinkStatus === UpdateStatus.Updating || deviceLinkStatus === UpdateStatus.FailedToUpdate) {
            return null;
        } else if (state.devices.length === 0) {
            return (
                <NoActiveDevicesPrompt isVisible={true} triggerCantFindDeviceDialog={() => cannotFindDeviceClick()}>
                    {cannotFindDeviceWizardVisible ?
                        <CannotFindDeviceWizard
                            onFinishClick={() => setCannotFindDeviceWizardVisible(false)}
                        /> : null}
                </NoActiveDevicesPrompt>
            );
        } else {
            return state.devices.map((device, index) =>
                <div key={device.serial + "-I" + index || "Unmapped" + index} className="col-md-4">
                    <DeviceCard device={device} handleClick={() => activeDeviceClick(device)} />
                </div>
            );
        }
    }

    const renderOmniCards = () => {
        const deviceTypeGroups = groupBy(state.devices, 'name');

        const omniCards = [];
        for (const deviceTypeGroup in deviceTypeGroups) {
            if (deviceTypeGroups[deviceTypeGroup].length > 1 && deviceTypeGroup === 'VitaloJAK 1') { // TODO: In future we may want to support bulk other device types
                omniCards.push(<div key={deviceTypeGroup} className="col-md-4"><OmniCard deviceTypeName={deviceTypeGroup} handleClick={() => omniCardClick(deviceTypeGroups[deviceTypeGroup][0].type)} /></div>)
            }
        }

        return omniCards;
    }

    return (
        <DocumentTitle title="Devices | Vitalograph Web Portal">
            <DevicesStateContextProvider state={state}>
                <div className="row">
                    <div className="col-md-6">
                        <h2 className='text-primary'>Active Devices</h2>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <DeviceEncryptionAlert show={isVitalojakEnabled()} />
                        {renderDevices()}
                        {renderOmniCards()}
                    </div>
                </div>
                <DeviceInteractionModal dispatch={dispatch} />
                <OmniInteractionModal dispatch={dispatch} />
                {renderPairingModal()}
            </DevicesStateContextProvider>
        </DocumentTitle>
    );
};
